import type { ChildAccidentMissingInformationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/missingInformation';
import type { FieldEventDataProps } from '@flos/react-ui/build/core/forms/address-field/address-field';
import { useRecoilState } from 'recoil';
import { missingInformationAtom } from '../../../recoil/atoms/missingInformationAtom';
import { AddressField, Box, CprField, Grid, GridItem, validateCpr } from '@flos/react-ui';
import { useHippoText } from '../../../hooks/useHippoText';
import { useState } from 'react';
import { isContainingDuplicateCPR } from '../configs/ValidateMissingDTOConfig';
import { getDawaIdFromkvhxUrl } from '../../../utils/addressUtil';
import { replaceAllCharactersButNumbers } from '../../../utils/stringUtil';
import { FieldBox } from './missingInformationCoverables/FieldBox';

const ChildAccidentMissingInformationForm = ({
    childAccidentMissingInformation
}: {
    childAccidentMissingInformation: ChildAccidentMissingInformationDTO;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [missingInformation, setMissingInformation] = useRecoilState(missingInformationAtom);
    const {
        cpr,
        fixedId,
        address,
        isDummyContact,
        fullName = ''
    } = childAccidentMissingInformation;
    const [cprNr, setCprNr] = useState(
        !isDummyContact && cpr ? replaceAllCharactersButNumbers(cpr) : undefined
    );
    const isDuplicate = isContainingDuplicateCPR(missingInformation.childAccident ?? []);
    const [providedAddress, setProvidedAddress] = useState(isDummyContact ? undefined : address);
    const [isAddressValid, setIsAddressValid] = useState<boolean | undefined>(
        !isDummyContact && !!address
    );

    const updateMissingInformationProperty = (
        updateValue: ChildAccidentMissingInformationDTO
    ): void => {
        setMissingInformation((previousState) => {
            const updateDto = previousState.childAccident?.map(
                (item: ChildAccidentMissingInformationDTO) => {
                    if (item.fixedId !== fixedId) return item;
                    return {
                        ...item,
                        ...updateValue
                    };
                }
            );
            return {
                ...previousState,
                childAccident: updateDto
            };
        });
    };

    const updateCpr = (value: string): void => {
        const formattedCPR = replaceAllCharactersButNumbers(value);
        setCprNr(formattedCPR);
        if (cpr !== formattedCPR) {
            updateMissingInformationProperty({
                cpr: formattedCPR
            });
        }
    };

    const updateAddress = (fieldEventDataProps: FieldEventDataProps): void => {
        const { address, kvhxUrl, status } = fieldEventDataProps;
        const dawaId = getDawaIdFromkvhxUrl(kvhxUrl);
        setIsAddressValid(status === 'valid');
        setProvidedAddress(address);
        updateMissingInformationProperty({ address, dawaId });
    };

    return (
        <Box>
            <Grid gap={4}>
                <GridItem span={12}>
                    <h6>Børneforsikring</h6>
                </GridItem>
                <FieldBox label={fullName}>
                    <CprField
                        label={getHippoText('DSS-ADDITIONAL-INFORMATION-CHILDCPR')}
                        value={cprNr}
                        onBlur={(event) => {
                            updateCpr(event.target.value);
                        }}
                        isValid={
                            cprNr !== undefined ? validateCpr(cprNr) && !isDuplicate : undefined
                        }
                        errorText={
                            isDuplicate
                                ? getHippoText('DSS-ADDITIONAL-INFORMATION-DUPLICATE-CPR')
                                : undefined
                        }
                        required
                    />
                </FieldBox>
                <FieldBox>
                    <AddressField
                        label={getHippoText('DSS-ADDITIONAL-INFORMATION-CHILDADDRESS')}
                        defaultValue={providedAddress}
                        fieldEvent={(data) => {
                            updateAddress(data);
                        }}
                        isValid={providedAddress !== undefined ? isAddressValid : undefined}
                        required
                    />
                </FieldBox>
            </Grid>
        </Box>
    );
};
export default ChildAccidentMissingInformationForm;
